import { useCallback, useState } from 'react'
import { type FieldErrors, useForm, UseFormRegister } from 'react-hook-form'
import type { InputFormValues, TransformedFormValues } from '../types'
import { salarySimulationFormSchema } from '@/domains/models/salarySimulation'
import { zodResolver } from '@hookform/resolvers/zod'
import { fireEvent } from '@/utils/activityLog'
import { payroll } from '../payroll'

type ReturnValue = {
  /** 給与の配列 */
  salarys: number[]
  /** フォームの登録を行う関数。 */
  register: UseFormRegister<InputFormValues>
  /** FormValuesの送信を行う関数。 */
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  /** 送信ボタンが活性化、非活性化 */
  isError: boolean
  /** エラー情報 */
  errors: FieldErrors<InputFormValues>
}

/**
 * Formのstateを管理するカスタムフック。
 *
 * @returns @see {@link ReturnValue}
 */
export const useFormState = (): ReturnValue => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<InputFormValues, unknown, TransformedFormValues>({
    mode: 'onChange',
    defaultValues: {
      calculationType: 'monthlyIncome',
      leaderProject: 'inPlanning',
      clientBillingRate: 70,
    },
    resolver: zodResolver(salarySimulationFormSchema),
  })
  const [salarys, SetSalarys] = useState<number[]>([])

  const onSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      handleSubmit((data) => {
        const { calculationType, leaderProject, clientBillingRate } = data
        fireEvent({
          event: 'clickSalaryCalculationButton',
          parameters: {
            calculation_type: calculationType,
            leader_project: leaderProject,
            client_billing_rate: clientBillingRate,
          },
        })
        SetSalarys(payroll(data))
      })()
    },
    [handleSubmit],
  )

  const isError = Object.keys(errors).length > 0

  return {
    salarys,
    register,
    onSubmit,
    isError,
    errors,
  }
}
